import { IItem, IItemProperty } from "../interfaces/IItem";
import { IMfilesObject as IMFilesObject, IMFilesProperty, IObjVer, IMfilesObject } from "../interfaces/mfiles/IMFilesObject";
import { IStartEndProperties, IConfiguration, IObjectTypeConfigurationItem, ITimeDefaults } from "../interfaces/configuration/IConfiguration";
import {
  fetchObjectsUrl,
  fetchObjectPropertiesUrl,
  fetchSpecificObjectUrl,
  fetchViewObjectsUrl,
  fetchPropertiesOfMultipleObjectsUrl,
  fetchCheckedoutObjectsUrl,
  fetchObjectsByExportedSearch,
  fetchObjectVersionPropertiesUrl,
  parseTokenFromQueryString,
} from "../helpers/urlHelper";

import { AxiosInstance, AxiosResponse } from "axios";
import { FilterOperator } from "../constants/filterOperators";
import { IMfilesPropertyFilter } from "../interfaces/filters/IFilter";
import { ICheckedoutObject, IItemsResponse, ISelectedItemsResponse } from "../interfaces/responses/IItemsResponse";
import { MFILES_DATE_VALUE_FORMAT, MFILES_REST_SEARCH_FORMAT } from "../constants/common";
import { format } from "date-fns";
import { getProperties, getStatus } from "../helpers/itemHelpers";
import { mergeDateAndTime, getLargestDateRangeFromItems } from "../helpers/dateHelpers";
import toNumber from "lodash/toNumber";
import uniqBy from "lodash/uniqBy";
import { mapMfilesPropertyToGanttProperty } from "../helpers/propertyHelpers";
import { ITEM_DEFAULT_COLOR } from "src/gantt/helpers/Const";
import { BuiltInPropertyDef } from "../constants/builtinProperties";
import ganttDB from "../storage/database";
import { IMFilesResult } from "../interfaces/mfiles/IMFilesResult";

export const getItemsAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<IMFilesResult<IMfilesObject>> => {
  const [responsesWithDateRange,responsesWithoutEndDate,responsesWithoutStartDate] = await Promise.all([
    fetchItemsFromDateRangeAsync(httpClient, configuration, dateRange, modifiedSince, limit),
    fetchItemsWithoutEndDateAsync(httpClient, configuration, dateRange, modifiedSince, limit),
    fetchItemsWithoutStartDateAsync(httpClient, configuration, modifiedSince, limit)
  ]);
  const allItemsResponses = responsesWithDateRange.concat(responsesWithoutEndDate).concat(responsesWithoutStartDate);
  const moreResults = allItemsResponses.some(response => response.MoreResults);
  const allItems = allItemsResponses.flatMap(response => response.Items);
  const uniqueItems = uniqBy(allItems, (i) => `${i.ObjVer.Type}-${i.ObjVer.ID}`);
  return Promise.resolve({ Items: uniqueItems, MoreResults: moreResults });
};

export const getItemsWithPropertiesAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<IItemsResponse> => {
  const [responsesWithDateRange,responsesWithoutEndDate,responsesWithoutStartDate] = await Promise.all([
    fetchItemsWithPropertiesFromDateRangeAsync(httpClient, configuration, dateRange, modifiedSince, limit),
    fetchItemsWithPropertiesWithoutEndDateAsync(httpClient, configuration, dateRange, modifiedSince, limit),
    fetchItemsWithPropertiesWithoutStartDateAsync(httpClient, configuration, modifiedSince, limit)
  ]);
  const allItemsResponses = responsesWithDateRange.concat(responsesWithoutEndDate).concat(responsesWithoutStartDate);
  const moreResults = allItemsResponses.some(response => response.moreResults);
  let allItems = allItemsResponses.flatMap(response => response.items);

  const relatedItems = await fetchRelatedItems(httpClient, configuration, allItems);
  allItems = allItems.concat(relatedItems);

  const uniqueItems = uniqBy(allItems, (i: IItem) => `${i.objectType}-${i.objectId}`);
  const allItemsWithIds = uniqueItems.map((item, index) => ({ ...item, id: index }));

  return Promise.resolve({ items: allItemsWithIds, moreResults: moreResults });
};

export const getSelectedItemsAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  limit: number,
  selectedItems: Map<number, number[]>,
  dateRange: [Date, Date]
): Promise<ISelectedItemsResponse> => {
  // Fetch only those of selected items that match the view configurations
  const objectTypesOfViews = Array.from(configuration.objectTypeConfigurations.entries());
  let items: IItem[] = [];
  let filteredItems : IItem[] = [];
  let fetchAlwaysItems : IItem[] = [];
  for (const [objectTypeId, objectTypeConfiguration] of objectTypesOfViews) {
    const objectIds = selectedItems.get(objectTypeId);
    if (objectIds) {
      const fetchedItems = await fetchSpecificItemsWithPropertiesAsync(httpClient, objectTypeConfiguration, objectTypeId, objectIds);
      filteredItems = filteredItems.concat(fetchedItems);
    } else if (objectTypeConfiguration.fetchAlways) {
      const filters = createObjectTypeConfigFilter(objectTypeConfiguration);
      const fetchedItems = await fetchItemsWithPropertiesAsync(httpClient, objectTypeConfiguration, filters, limit);
      fetchAlwaysItems = fetchAlwaysItems.concat(fetchedItems.items);
    }
  }

  items = filteredItems.concat(fetchAlwaysItems);

  if(dateRange) {
    items = filterItemsWithStartAndEnd(items, dateRange);
  }

  const relatedItems = await fetchRelatedItems(httpClient, configuration, items);
  items = items.concat(relatedItems);

  // Set frontend ids (index in all data)
  const itemsWithIds = items.map((item, index) => ({ ...item, id: index }));

  // Daterange is in this case defined by what the actual items are: earliest start to latest end of items.
  // If either start or end is null, use a reasonable fallback.
  const itemsDateRange = getLargestDateRangeFromItems(filteredItems, configuration.maxDateRangeInYears);

  return {
    items: itemsWithIds,
    moreResults: false,
    dateRange: itemsDateRange,
  } as ISelectedItemsResponse;
};

export const getViewItemsAsync = async (
  httpClient: AxiosInstance, 
  configuration: IConfiguration, 
  path: string, 
  limit: number, 
): Promise<IMFilesResult<IMFilesObject>> => {
  const url = fetchViewObjectsUrl(path, limit);
  const itemsResponse = await httpClient.get<IMFilesResult<IMFilesObject>>(url);
  const validObjectTypes: number[] = [];
  for (const objectTypeConfiguration of Array.from(configuration.objectTypeConfigurations.values())) {
    if(!objectTypeConfiguration.fetchAlways) {
      validObjectTypes.push(objectTypeConfiguration.objectTypeId);
    }
  }
  return {
    Items: itemsResponse.data.Items.filter(f => validObjectTypes.includes(f.ObjVer.Type)),
    MoreResults: itemsResponse.data.MoreResults
  };
};

export const getViewItemsWithPropertiesAsync = async (
  httpClient: AxiosInstance, 
  configuration: IConfiguration, 
  path: string, 
  limit: number, 
  dateRange: [Date, Date]
): Promise<IItem[]> => {
  const viewItems = await getViewItemsAsync(httpClient, configuration, path, limit);
  const mfilesObjects = viewItems.Items;
  const objVersOfItems = mfilesObjects.map((mfo) => mfo.ObjVer);
  const propertiesOfMultipleObjects = await getPropertiesOfMultipleObjectsAsync(httpClient, objVersOfItems);
  let items = mapMFilesItemWithPropertiesToItem(configuration.objectTypeConfigurations, mfilesObjects, propertiesOfMultipleObjects);
  if(dateRange) {
    items = filterItemsWithStartAndEnd(items, dateRange);
  }

  for (const objectTypeConfiguration of Array.from(configuration.objectTypeConfigurations.values())) {
    if (objectTypeConfiguration.fetchAlways) {
      const filters = createObjectTypeConfigFilter(objectTypeConfiguration);
      const fetchedItems = await fetchItemsWithPropertiesAsync(
        httpClient,
        objectTypeConfiguration,
        filters,
        configuration.fetchedItemsCountLimit
      );
      items = items.concat(fetchedItems.items);
    }
  }

  const relatedItems = await fetchRelatedItems(httpClient, configuration, items);
  items = items.concat(relatedItems);

  const uniqueItems = uniqBy(items, (item) => `${item.objectType}-${item.objectId}`);
  return uniqueItems.map((item, index) => ({ ...item, id: index }));
};

export const getSearchItemsAsync = async (
  httpClient: AxiosInstance, 
  configuration: IConfiguration, 
  searchParam: string, 
  limit: number,
): Promise<IMFilesResult<IMFilesObject>> => {
  const url = fetchObjectsByExportedSearch(searchParam, limit);
  const itemsResponse = await httpClient.get<IMFilesResult<IMFilesObject>>(url);
  const validObjectTypes: number[] = [];
  for (const objectTypeConfiguration of Array.from(configuration.objectTypeConfigurations.values())) {
    if(!objectTypeConfiguration.fetchAlways) {
      validObjectTypes.push(objectTypeConfiguration.objectTypeId);
    }
  }
  return { 
    Items: itemsResponse.data.Items.filter(f => validObjectTypes.includes(f.ObjVer.Type)),
    MoreResults: itemsResponse.data.MoreResults
  };
};

export const getSearchItemsWithPropertiesAsync = async (
  httpClient: AxiosInstance, 
  configuration: IConfiguration, 
  searchParam: string, 
  limit: number,
  dateRange: [Date, Date]
): Promise<IItem[]> => {
  const searchResults = await getSearchItemsAsync(httpClient, configuration, searchParam, limit);
  const mfilesObjects = searchResults.Items;
  const objVersOfItems = mfilesObjects.map((mfo) => mfo.ObjVer);
  const propertiesOfMultipleObjects = await getPropertiesOfMultipleObjectsAsync(httpClient, objVersOfItems);
  let items = mapMFilesItemWithPropertiesToItem(configuration.objectTypeConfigurations, mfilesObjects, propertiesOfMultipleObjects);

  for (const objectTypeConfiguration of Array.from(configuration.objectTypeConfigurations.values())) {
    if (objectTypeConfiguration.fetchAlways) {
      const filters = createObjectTypeConfigFilter(objectTypeConfiguration);
      const fetchedItems = await fetchItemsWithPropertiesAsync(
        httpClient,
        objectTypeConfiguration,
        filters,
        configuration.fetchedItemsCountLimit
      );
      items = items.concat(fetchedItems.items);
    }
  }

  if(dateRange) {
    items = filterItemsWithStartAndEnd(items, dateRange);
  }

  const relatedItems = await fetchRelatedItems(httpClient, configuration, items);
  items = items.concat(relatedItems);

  const uniqueItems = uniqBy(items, (item) => `${item.objectType}-${item.objectId}`);
  return uniqueItems.map((item, index) => ({ ...item, id: index }));
};

export const fetchChangedItemsAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  modifiedSince: Date,
  limit: number
): Promise<Array<IMFilesObject>> => {
  const items = Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).map(async (objectTypeConfig) => {
      const filters = createModifiedSinceFilter(modifiedSince);
      const fetchItemsUrl = fetchObjectsUrl(objectTypeConfig.objectTypeId, filters, limit);
      const itemsResponse = await httpClient.get<IMFilesResult<IMFilesObject>>(fetchItemsUrl);
      return itemsResponse.data.Items;
    })
  );
  return (await items).flatMap((i) => i);
}

export const fetchCheckedoutItems = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
): Promise<Array<ICheckedoutObject>> => {
  const items = Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).map(async (objectTypeConfig) => {
      const url = fetchCheckedoutObjectsUrl();
      const response = await httpClient.post(url, objectTypeConfig.objectTypeId);
      return response.data as ICheckedoutObject[];
    })
  );
  return (await items).flatMap((i) => i);
};

const fetchItemsFromDateRangeAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<Array<IMFilesResult<IMfilesObject>>> => {

  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).map((objectTypeConfig) => {
      const filters = createDateRangeFilter(objectTypeConfig, dateRange, modifiedSince);
      return fetchItemsAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsWithPropertiesFromDateRangeAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<Array<IItemsResponse>> => {
  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).map((objectTypeConfig) => {
      const filters = createDateRangeFilter(objectTypeConfig, dateRange, modifiedSince);
      return fetchItemsWithPropertiesAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsWithoutEndDateAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<Array<IMFilesResult<IMfilesObject>>> => {
  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).filter(objectTypeConfig => objectTypeConfig.dateProperties && objectTypeConfig.dateProperties.length > 1).map((objectTypeConfig) => {
      const filters = createWithoutEndDateFilter(objectTypeConfig, dateRange, modifiedSince);
      return fetchItemsAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsWithPropertiesWithoutEndDateAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  dateRange: [Date, Date],
  modifiedSince: Date,
  limit: number
): Promise<Array<IItemsResponse>> => {
  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).filter(objectTypeConfig => objectTypeConfig.dateProperties && objectTypeConfig.dateProperties.length > 1).map((objectTypeConfig) => {
      const filters = createWithoutEndDateFilter(objectTypeConfig, dateRange, modifiedSince);
      return fetchItemsWithPropertiesAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsWithoutStartDateAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  modifiedSince: Date,
  limit: number
): Promise<Array<IMFilesResult<IMfilesObject>>> => {
  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).filter(objectTypeConfig => objectTypeConfig.dateProperties && objectTypeConfig.dateProperties.length > 1 && objectTypeConfig.fetchEmptyDates).map((objectTypeConfig) => {
      const filters = createWithoutStartDateFilter(objectTypeConfig, modifiedSince);
      return fetchItemsAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsWithPropertiesWithoutStartDateAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  modifiedSince: Date,
  limit: number
): Promise<Array<IItemsResponse>> => {
  return Promise.all(
    Array.from(configuration.objectTypeConfigurations.values()).filter(objectTypeConfig => objectTypeConfig.dateProperties && objectTypeConfig.dateProperties.length > 1 && objectTypeConfig.fetchEmptyDates).map((objectTypeConfig) => {
      const filters = createWithoutStartDateFilter(objectTypeConfig, modifiedSince);
      return fetchItemsWithPropertiesAsync(httpClient, objectTypeConfig, filters, limit);
    })
  );
};

const fetchItemsAsync = async (
  httpClient: AxiosInstance,
  objectTypeConfig: IObjectTypeConfigurationItem,
  filters: Array<IMfilesPropertyFilter>,
  limit: number
):  Promise<IMFilesResult<IMFilesObject>> => {
  const fetchItemsUrl = fetchObjectsUrl(objectTypeConfig.objectTypeId, filters, limit);
  const itemsResponse = await httpClient.get<IMFilesResult<IMFilesObject>>(fetchItemsUrl);
  return itemsResponse.data;
};

const fetchItemsWithPropertiesAsync = async (
  httpClient: AxiosInstance,
  objectTypeConfig: IObjectTypeConfigurationItem,
  filters: Array<IMfilesPropertyFilter>,
  limit: number
): Promise<IItemsResponse> => {
  const itemsResponse = await fetchItemsAsync(httpClient,objectTypeConfig,filters,limit);
  const mfilesObjects = itemsResponse.Items;
  const objVersOfItems = mfilesObjects.map((mfo) => mfo.ObjVer);
  const propertiesOfMultipleObjects = await getPropertiesOfMultipleObjectsAsync(httpClient, objVersOfItems);
  const objectTypeConfigurations = new Map<number,IObjectTypeConfigurationItem>();
  objectTypeConfigurations.set(objectTypeConfig.objectTypeId, objectTypeConfig);
  const items = mapMFilesItemWithPropertiesToItem(objectTypeConfigurations, mfilesObjects, propertiesOfMultipleObjects);
  return { items: items, moreResults: itemsResponse.MoreResults };
};

const fetchSpecificItemsWithPropertiesAsync = async (
  httpClient: AxiosInstance,
  objectTypeConfig: IObjectTypeConfigurationItem,
  objectType: number,
  objectIds: number[]
): Promise<IItem[]> => {
  const items = await Promise.all(
    objectIds.map(async (id) => {
      try {
        const fetchObjectUrl = fetchSpecificObjectUrl(objectType, id);
        const itemResponse = await httpClient.get<IMFilesObject>(fetchObjectUrl);
        const mfilesObject = itemResponse.data;
        return await fetchSpecificItemPropertiesAsync(httpClient, objectTypeConfig, mfilesObject);
      } catch (e) {
        //Sometimes getting latest version returns HTTP 500, maybe object is deleted?
        return null;
      }
    })
  );
  return items.filter(i => i !== null);
};

export const fetchSpecificItemPropertiesAsync = async (
  httpClient: AxiosInstance,
  objectTypeConfig: IObjectTypeConfigurationItem,
  mfilesObject: IMFilesObject
): Promise<IItem> => {
  let properties: IItemProperty[] = [];
  const cachedItem = await ganttDB.items.get([mfilesObject.ObjVer.Type, mfilesObject.ObjVer.ID]);
  if(cachedItem?.objectVersion === mfilesObject.ObjVer.Version) {
    properties = cachedItem.properties;
  } else {
    const fetchPropertiesUrl = fetchObjectPropertiesUrl(mfilesObject.ObjVer.Type, mfilesObject.ObjVer.ID);
    const propertiesResponse = await httpClient.get<IMFilesProperty[]>(fetchPropertiesUrl);
    const mfProperties = propertiesResponse.data;
    properties = mfProperties.map(mapMfilesPropertyToGanttProperty);
    ganttDB.items.put({
      objectId: mfilesObject.ObjVer.ID,
      objectType: mfilesObject.ObjVer.Type,
      objectVersion: mfilesObject.ObjVer.Version,
      properties: properties,
    });
  }
  const [start, end] = getItemStartAndEnd(objectTypeConfig.dateProperties, objectTypeConfig.timeProperties, objectTypeConfig.timeDefaults, properties);
  const duration = getDuration(objectTypeConfig.durationProperty, objectTypeConfig.durationUnit, properties);
  return mapMFilesItemToItem(mfilesObject, properties, start, end, duration);
};

const fetchRelatedItems = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  items: IItem[],
): Promise<IItem[]> => {

  let relatedItems: IItem[] = [];
  const promises: Promise<unknown>[] = [];

  for(const objectTypeConfig of configuration.objectTypeConfigurations.values()) {
    //Check if type is parent type
    if(objectTypeConfig.editConfig?.childObjectTypesAndProperties?.length > 0) {
      //List parent ids
      const parentIds: number[] = [];
      for(const item of items) {
        if(item.objectType === objectTypeConfig.objectTypeId) {
          parentIds.push(item.objectId);
        }
      }
      //Loop child relations
      for(const [relatedType,relatedProperty] of objectTypeConfig.editConfig?.childObjectTypesAndProperties ?? []) {
        const relatedTypeConfig: IObjectTypeConfigurationItem = configuration.objectTypeConfigurations.get(relatedType);
        //Fetch missing children for parent
        if(relatedTypeConfig.fetchRelated && parentIds.length > 0) {
          //Split parent ids to batches size of 50 to avoid url growing too long
          const batchSize = 50;
          for (let i = 0; i < parentIds.length; i += batchSize) {
            const batch = parentIds.slice(i, i + batchSize);
            //Create property filter containing parent ids
            const filters: IMfilesPropertyFilter[] = [];
            filters.push({
              name: "Parent id filter",
              propertyId: relatedProperty,
              operator: FilterOperator.Equal,
              value: batch.join(",")
            });
            promises.push(fetchItemsWithPropertiesAsync(httpClient, relatedTypeConfig, filters, configuration.fetchedItemsCountLimit));
          }
        }
        //Fetch missing parents for children
        if(objectTypeConfig.fetchRelated) {
          const missingParentIds: number[] = [];
          //Loop related items
          for(const item of items) {
            if(item.objectType === relatedType) {
              //Get reference to parent id
              const prop = item.properties.find((p) => p.property === relatedProperty);
              if(prop) {
                const relatedId = toNumber(prop.value);
                //Set parent to be fetched if it is not loaded yet
                if(!parentIds.includes(relatedId) && !missingParentIds.includes(relatedId)) {
                  missingParentIds.push(relatedId);
                }
              }
            }
          }
          if(missingParentIds.length > 0) {
            promises.push(fetchSpecificItemsWithPropertiesAsync(httpClient, objectTypeConfig, objectTypeConfig.objectTypeId, missingParentIds));
          }
        }
      }
    }
  }

  if(promises.length > 0) {
    const responses = await Promise.all(promises);
    for(const response of responses) {
      const responseItems = response as IItem[];
      if(responseItems !== null && responseItems.length > 0) {
        relatedItems = relatedItems.concat(responseItems);
      }
      const itemResponse = response as IItemsResponse;
      if(itemResponse?.items?.length > 0) {
        relatedItems = relatedItems.concat(itemResponse.items);
      }
    }
  }

  return relatedItems;
};

export const getPropertiesOfMultipleObjectsAsync = async (httpClient: AxiosInstance, objVers: IObjVer[]): Promise<IItemProperty[][]> => {

  //Fallback to nobatch version, since readonly session can't handle post-methods
  if(parseTokenFromQueryString()) {
    return getPropertiesOfMultipleObjectsNoBatchAsync(httpClient, objVers);
  }

  const allProperties: IItemProperty[][] = [];
  const cachedProperties: IItemProperty[][] = [];
  let nonCachedProperties: IItemProperty[][] = [];
  const isPropertyCached: boolean[] = [];
  const propertyIndex: number[] = [];
  const nonCachedObjVers: IObjVer[] = [];

  for(let i = 0; i < objVers.length; i++) {
    const objVer = objVers[i];
    const cachedItem = await ganttDB.items.get([objVer.Type, objVer.ID]);
    if(cachedItem?.objectVersion === objVer.Version) {
      isPropertyCached[i] = true;
      propertyIndex[i] = cachedProperties.length;
      cachedProperties.push(cachedItem.properties);
    } else {
      isPropertyCached[i] = false;
      propertyIndex[i] = nonCachedObjVers.length;
      nonCachedObjVers.push(objVer);
    }
  }

  if(nonCachedObjVers.length === 0) {
    return cachedProperties;
  }

  const batchSize = 100;
  const batchObjVers: IObjVer[][] = [];
  for(let i = 0; i < nonCachedObjVers.length; i++) {
    const batchId = Math.floor(i / batchSize);
    if(batchObjVers.length < (batchId + 1)) {
      batchObjVers.push([]);
    }
    batchObjVers[batchId].push(nonCachedObjVers[i]);
  }

  const propertiesPromises: Promise<AxiosResponse>[] = [];
  const url = fetchPropertiesOfMultipleObjectsUrl();
  for(const batch of batchObjVers) {
    propertiesPromises.push(httpClient.post(url, batch));
  }
  const propertiesResponses = await Promise.all(propertiesPromises);

  for(const propertiesResponse of propertiesResponses) {
    const propertiesOfObjects = propertiesResponse.data as IMFilesProperty[][];
    const properties = propertiesOfObjects.map((propertiesOfSingleObject) => propertiesOfSingleObject.map(mapMfilesPropertyToGanttProperty));
    nonCachedProperties = nonCachedProperties.concat(properties);
  }

  for(let i = 0; i < objVers.length; i++) {
    const objVer = objVers[i];
    if(isPropertyCached[i]) {
      allProperties.push(cachedProperties[propertyIndex[i]]);
    } else {
      allProperties.push(nonCachedProperties[propertyIndex[i]]);
      ganttDB.items.put({
        objectId: objVer.ID,
        objectType: objVer.Type,
        objectVersion: objVer.Version,
        properties: nonCachedProperties[propertyIndex[i]],
      });
    }
  }

  return allProperties;
};

export const getPropertiesOfMultipleObjectsNoBatchAsync = async (httpClient: AxiosInstance, objVers: IObjVer[]): Promise<IItemProperty[][]> => {

  const allProperties: IItemProperty[][] = [];
  const cachedProperties: IItemProperty[][] = [];
  const nonCachedProperties: IItemProperty[][] = [];
  const isPropertyCached: boolean[] = [];
  const propertyIndex: number[] = [];
  const nonCachedObjVers: IObjVer[] = [];

  for(let i = 0; i < objVers.length; i++) {
    const objVer = objVers[i];
    const cachedItem = await ganttDB.items.get([objVer.Type, objVer.ID]);
    if(cachedItem?.objectVersion === objVer.Version) {
      isPropertyCached[i] = true;
      propertyIndex[i] = cachedProperties.length;
      cachedProperties.push(cachedItem.properties);
    } else {
      isPropertyCached[i] = false;
      propertyIndex[i] = nonCachedObjVers.length;
      nonCachedObjVers.push(objVer);
    }
  }

  if(nonCachedObjVers.length === 0) {
    return cachedProperties;
  }

  const propertiesPromises: Promise<AxiosResponse>[] = [];
  for(const objVer of nonCachedObjVers) {
    const url = fetchObjectVersionPropertiesUrl(objVer.Type, objVer.ID, objVer.Version);
    propertiesPromises.push(httpClient.get(url));
  }
  const propertiesResponses = await Promise.all(propertiesPromises);
  for(const propertiesResponse of propertiesResponses) {
    const propertiesOfSingleObject = propertiesResponse.data as IMFilesProperty[];
    const properties = propertiesOfSingleObject.map(mapMfilesPropertyToGanttProperty);
    nonCachedProperties.push(properties);
  }

  for(let i = 0; i < objVers.length; i++) {
    const objVer = objVers[i];
    if(isPropertyCached[i]) {
      allProperties.push(cachedProperties[propertyIndex[i]]);
    } else {
      allProperties.push(nonCachedProperties[propertyIndex[i]]);
      ganttDB.items.put({
        objectId: objVer.ID,
        objectType: objVer.Type,
        objectVersion: objVer.Version,
        properties: nonCachedProperties[propertyIndex[i]],
      });
    }
  }

  return allProperties;
};

const filterItemsWithStartAndEnd = (items: IItem[], dateRange: [Date, Date]): IItem[]  => {
  if(!dateRange) {
    return items;
  }
  return items.filter(i => 
    //no start and no end (employees?)
    (!i.start && !i.end) ||
    //both start and end
    (i.start && i.end && i.start <= dateRange[1] && i.end >= dateRange[0]) ||
    //only start and no end
    (i.start && !i.end && i.start >= dateRange[0] && i.start <= dateRange[1])
  );
};

/**
 * Duration in some cases can be 0
 * If duration is not set, but durationUnit === "h" ==> duration minimum is set to 1 hour
 * @param durationProperty 
 * @param durationUnit 
 * @param properties 
 * @returns 
 */
const getDuration = (
  durationProperty: number,
  durationUnit: string,
  properties: IItemProperty[]
): number => {
  let duration = null;
  if (durationProperty) {
    duration = properties.find(i => i.property === durationProperty)?.value;

    if ( durationUnit === "h" ) {
      if ( duration ) {
        duration = duration / 24;
      } else {
        duration = 1 / 24; // default 1 hour
      }
    }
  }
  return duration;
}

const getItemStartAndEnd = (
  dateProperties: IStartEndProperties,
  timeProperties: IStartEndProperties,
  timeDefaults: ITimeDefaults,
  properties: IItemProperty[]
): [Date, Date] => {
  // M-files returns dates in local time zone while including the Z-character signifying UTC time.
  // For this reason we remove the last character from each time string.
  if (dateProperties == null || dateProperties.length < 2) {
    return [null, null];
  }
  const [startDatePropertyId, endDatePropertyId] = dateProperties;
  let startDate = null;
  let endDate = null;
  const [startDateProperty, endDateProperty] = getProperties(properties, [startDatePropertyId, endDatePropertyId]);
  if (startDateProperty?.value) {
    const startDateString = startDateProperty.value.substring(0, startDateProperty.value.length - 1);
    startDate = new Date(startDateString);
  }
  if (endDateProperty?.value) {
    const endTimeString = endDateProperty.value.substring(0, endDateProperty.value.length - 1);
    endDate = new Date(endTimeString);
  }
  if (timeProperties == null || timeProperties.length < 2) {
    if(timeDefaults != null && timeDefaults.length === 2) {
      const [defaultStartTime, defaultEndTime] = timeDefaults;
      return [mergeDateAndTime(startDate, defaultStartTime), mergeDateAndTime(endDate, defaultEndTime)];
    }
    return [startDate, endDate];
  }

  const [startTimeProperty, endTimeProperty] = getProperties(properties, [...timeProperties]);
  let startTime = null;
  let endTime = null;
  if (startTimeProperty?.value) {
    const startTimeString = startTimeProperty.value.substring(0, startTimeProperty.value.length - 1);
    startTime = new Date(startTimeString);
  }
  if (endTimeProperty?.value) {
    const endTimeString = endTimeProperty.value.substring(0, endTimeProperty.value.length - 1);
    endTime = new Date(endTimeString);
  }
  return [mergeDateAndTime(startDate, startTime), mergeDateAndTime(endDate, endTime)];
};

const mapMFilesItemToItem = (mfItem: IMFilesObject, properties: IItemProperty[], start: Date, end: Date, duration: number): IItem => {
  const itemStatus = getStatus(properties);
  return {
    id: -1,
    objectId: mfItem.ObjVer.ID,
    objectType: mfItem.ObjVer.Type,
    objectVersion: mfItem.ObjVer.Version,
    objectGuid: mfItem.ObjectGUID,
    properties: properties,
    name: mfItem.Title,
    linksTo: [],
    linksFrom: [],
    start: start,
    end: end,
    duration: duration,
    color: ITEM_DEFAULT_COLOR,
    isCheckedOut: mfItem.ObjectCheckedOut,
    isCheckedOutToThisUser: mfItem.ObjectCheckedOutToThisUser,
    status: itemStatus,
  };
};

export const mapMFilesItemWithPropertiesToItem = (objectTypeConfigurations: Map<number,IObjectTypeConfigurationItem>, mfilesObjects: IMFilesObject[], propertiesOfMultipleObjects: IItemProperty[][]): IItem[] => {
  return mfilesObjects.map((mfo, i) => {
    const properties = propertiesOfMultipleObjects[i];
    const objectTypeConfig = objectTypeConfigurations.get(mfo.ObjVer.Type);
    const [start, end] = getItemStartAndEnd(objectTypeConfig.dateProperties, objectTypeConfig.timeProperties, objectTypeConfig.timeDefaults, properties);
    const duration = getDuration(objectTypeConfig.durationProperty, objectTypeConfig.durationUnit, properties);
    return mapMFilesItemToItem(mfo, properties, start, end, duration);
  });
};

//This is used to prevent multiple filters on same property
//M-Files gives an error when same property has multiple filters with same operator
export const combineFilters = (filters: IMfilesPropertyFilter[], filter: IMfilesPropertyFilter) => {
  for(const existingFilter of filters.filter(i => i.propertyId === filter.propertyId)) {
    if(
      (filter.operator === FilterOperator.GreaterThan || filter.operator === FilterOperator.GreaterThanOrEqual) &&
      (existingFilter.operator === FilterOperator.GreaterThan || existingFilter.operator === FilterOperator.GreaterThanOrEqual)
    ) {
      if(existingFilter.value < filter.value) {
        existingFilter.operator = filter.operator;
        existingFilter.value = filter.value;
      } else if (existingFilter.value === filter.value && existingFilter.operator !== filter.operator) {
        existingFilter.operator = FilterOperator.GreaterThan;
      }
      return;
    }
    if(
      (filter.operator === FilterOperator.LessThan || filter.operator === FilterOperator.LessThanOrEqual) &&
      (existingFilter.operator === FilterOperator.LessThan || existingFilter.operator === FilterOperator.LessThanOrEqual)
    ) {
      if(existingFilter.value > filter.value) {
        existingFilter.operator = filter.operator;
        existingFilter.value = filter.value;
      } else if (existingFilter.value === filter.value && existingFilter.operator !== filter.operator) {
        existingFilter.operator = FilterOperator.LessThan;
      }
      return;
    }
  }
  filters.push(filter);
}

const createDateRangeFilter = (
  objectTypeConfig: IObjectTypeConfigurationItem,
  dateRange: [Date, Date],
  modifiedSince: Date,
): IMfilesPropertyFilter[] => {
  const filters = createObjectTypeConfigFilter(objectTypeConfig);
  if (objectTypeConfig.dateProperties && objectTypeConfig.dateProperties.length > 1) {
    combineFilters(filters, {
      name: "Start date filter",
      propertyId: objectTypeConfig.dateProperties[0],
      operator: FilterOperator.LessThanOrEqual,
      value: format(dateRange[1], MFILES_REST_SEARCH_FORMAT),
    });
    combineFilters(filters, {
      name: "End date filter",
      propertyId: objectTypeConfig.dateProperties[1],
      operator: FilterOperator.GreaterThanOrEqual,
      value: format(dateRange[0], MFILES_REST_SEARCH_FORMAT),
    });
  }
  return filters.concat(createModifiedSinceFilter(modifiedSince));
};

const createWithoutEndDateFilter = (
  objectTypeConfig: IObjectTypeConfigurationItem,
  dateRange: [Date, Date],
  modifiedSince: Date,
): IMfilesPropertyFilter[] => {
  const filters = createObjectTypeConfigFilter(objectTypeConfig);
  combineFilters(filters, {
    name: "Start filter",
    propertyId: objectTypeConfig.dateProperties[0],
    operator: FilterOperator.LessThanOrEqual,
    value: format(dateRange[1], MFILES_REST_SEARCH_FORMAT),
  });
  combineFilters(filters, {
    name: "End filter",
    propertyId: objectTypeConfig.dateProperties[0],
    operator: FilterOperator.GreaterThanOrEqual,
    value: format(dateRange[0], MFILES_REST_SEARCH_FORMAT),
  });
  combineFilters(filters, {
    name: "Empty end date filter",
    propertyId: objectTypeConfig.dateProperties[1],
    operator: FilterOperator.Equal,
    value: "%00",
  });
  return filters.concat(createModifiedSinceFilter(modifiedSince));
};

const createWithoutStartDateFilter = (
  objectTypeConfig: IObjectTypeConfigurationItem,
  modifiedSince: Date,
): IMfilesPropertyFilter[] => {
  const filters = createObjectTypeConfigFilter(objectTypeConfig);
  combineFilters(filters, {
    name: "Empty start date filter",
    propertyId: objectTypeConfig.dateProperties[0],
    operator: FilterOperator.Equal,
    value: "%00",
  });
  for(const modifiedFilter of createModifiedSinceFilter(modifiedSince)) {
    combineFilters(filters, modifiedFilter);
  }
  return filters;
};

const createModifiedSinceFilter = (
  modifiedSince: Date,
): IMfilesPropertyFilter[] => {
  const filters: IMfilesPropertyFilter[] = [];
  if(modifiedSince) {
    filters.push({
      name: "Modified since",
      propertyId: BuiltInPropertyDef.LastModified,
      operator: FilterOperator.GreaterThanOrEqual,
      value: format(modifiedSince, MFILES_DATE_VALUE_FORMAT)
    });
  }
  return filters;
};

const createObjectTypeConfigFilter = (
  objectTypeConfig: IObjectTypeConfigurationItem,
): IMfilesPropertyFilter[] => {
  const filters: IMfilesPropertyFilter[] = [];
  if(objectTypeConfig.filters) {
    for(const filter of objectTypeConfig.filters) {
      combineFilters(filters, {
        name: "Config filter",
        propertyId: filter.property,
        operator: filter.operator,
        value: filter.value
      });
    }
  }
  return filters;
};
